import { useEffect, useState } from 'react';
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

export function useThreeCore(mountRef) {
  const [threeCore, setThreeCore] = useState({
    scene: null,
    camera: null,
    renderer: null,
    composer: null,
    filmPass: null,
    bloomPass: null,
    controls: null,
  });

  useEffect(() => {
    if (!mountRef.current) return;
    const currentMount = mountRef.current;

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x001f2f);
    scene.fog = new THREE.FogExp2(0x001f2f, 0.02);

    // Camera
    const camera = new THREE.PerspectiveCamera(75, currentMount.clientWidth / currentMount.clientHeight, 0.1, 1000);
    camera.position.set(0, 0, 15);
    camera.lookAt(0, 0, 0);

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: "high-performance", preserveDrawingBuffer: true });
    renderer.setSize(currentMount.clientWidth, currentMount.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    currentMount.appendChild(renderer.domElement);

    // Camera Controls (orbit / zoom / pan + smooth damping)
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.rotateSpeed = 0.6;
    controls.zoomSpeed = 0.8;
    controls.enablePan = false;
    controls.enableZoom = true;
    controls.minDistance = 4;
    controls.maxDistance = 40;
    controls.target.set(0, 0, 0);
    controls.update();

    // Composer
    const composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    const bloomPass = new UnrealBloomPass(new THREE.Vector2(currentMount.clientWidth, currentMount.clientHeight), 1.5, 0.4, 0.85);
    bloomPass.strength = 1.0; bloomPass.radius = 0.8; bloomPass.threshold = 0.2;
    composer.addPass(bloomPass);
    
    const filmShader = {
      uniforms: { tDiffuse: { value: null }, time: { value: 0 }, intensity: { value: 0.05 } },
      vertexShader: `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
      fragmentShader: `uniform sampler2D tDiffuse; uniform float time; uniform float intensity; varying vec2 vUv;\n          float random(vec2 st) { return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123); }\n          void main() { vec4 color = texture2D(tDiffuse, vUv); float grain = random(vUv + time) * intensity; color.rgb += grain; gl_FragColor = color; }`
    };
    const filmPass = new ShaderPass(filmShader);
    composer.addPass(filmPass);

    setThreeCore({ scene, camera, renderer, composer, filmPass, bloomPass, controls });

    const handleResize = () => {
      if (!currentMount) return;
      const width = currentMount.clientWidth;
      const height = currentMount.clientHeight;

      // Update camera aspect ratio
      camera.aspect = width / height;
      camera.updateProjectionMatrix();

      // Update renderer and composer sizes
      renderer.setSize(width, height);
      composer.setSize(width, height);

      // Update pixel ratio
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    };

    // Add resize listener
    window.addEventListener('resize', handleResize);

    // Trigger initial resize
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      controls.dispose();
      if (currentMount && renderer.domElement) {
        currentMount.removeChild(renderer.domElement);
      }
      scene.traverse(object => {
        if (object.geometry) object.geometry.dispose();
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose());
          } else {
            object.material.dispose();
          }
        }
      });
      renderer.dispose();
    };
  }, [mountRef]);

  return threeCore;
}
